import type { CalculationOut, CaseDetailOut } from "@/api";
import { Button } from "@/components/Button";
import { FirstPaintScope, StateMessage } from "@/components/StateMessage";
import { formatDateTime } from "@/lib/format";

import { count, isReleased, isWallHint, needsRerunForStage } from "./readiness";

/**
 * Days after which the mirror's date gets a banner of its own. The mirror's
 * own staleness threshold (`STALE_AFTER_DAYS`, `pricing/price_source.py`,
 * controller notes §2) — a display threshold, not a rule: the age never
 * blocks (design §4.2, Zweitblick 4), the approval names the date either way.
 */
const PRICE_AGE_NOTICE_DAYS = 2;

/** What a person still has to do about walls without a measure (E-65) — before the release, not after. */
const WALLS_NOTICE_TAIL = "vor der Freigabe bestätigen, dass dort kein Rost liegt.";

/**
 * "Preis fehlt" (fix round 2, Important 2): `calc.gaps.length` counts price
 * *gaps*, not positions without a price — since N-16 a gap can sit on a
 * *priced* line (a soft code like `cut_rfq` on an otherwise-priced grating
 * line). Naming the gap count as "n Positionen ohne Preis" says something
 * false about a calculation with no truly unpriced line at all. The two
 * counted separately: `unpriced` (`vkTotal === null`, the lines this banner
 * may actually call priceless) and `remaining` (every other gap, priced but
 * still open) — one clause each, joined only when both apply.
 */
function gapCounts(calc: CalculationOut): { unpriced: number; remaining: number } {
  const unpriced = calc.lines.filter((line) => line.vkTotal === null).length;
  return { unpriced, remaining: calc.gaps.length - unpriced };
}

function gapLede({ unpriced, remaining }: { unpriced: number; remaining: number }): string {
  if (unpriced > 0) {
    const positions = `${count(unpriced, "Position", "Positionen")} ohne Preis`;
    return remaining > 0 ? `${positions}, ${count(remaining, "Zuschlag auf Anfrage", "Zuschläge auf Anfrage")}` : positions;
  }
  return `Alle Positionen bepreist, ${count(remaining, "Zuschlag offen", "Zuschläge offen")}`;
}

/** The still-open sentence — every gap named in `gapLede` is one to tick below. */
function gapOpenSentence(calc: CalculationOut): string {
  const counts = gapCounts(calc);
  return counts.remaining === 0
    ? `${gapLede(counts)}. Jede Lücke lässt sich unten quittieren — sie wird dann von Hand in JTL bepreist.`
    : `${gapLede(counts)} — jede Lücke lässt sich unten quittieren — sie wird dann von Hand in JTL bepreist.`;
}

/** The already-released sentence — the gap count in the tail is the review's own number (S-9), not `gapLede`'s split. */
function gapReleasedSentence(calc: CalculationOut): string {
  return `${gapLede(gapCounts(calc))} — freigegeben mit ${count(calc.gaps.length, "Lücke", "Lücken")}, wird von Hand in JTL bepreist.`;
}

/**
 * What stands above the figures (design §9): the mirror missing, the Vorgang
 * frozen, a stale Stand (with or without an approval that used to hold), a
 * changed price state, a missing price, an old price list. Status red where
 * the approval is blocked by more than a rerun, and always with the words
 * that say so; a rerun alone is the next step, neutral, with the red „Neu
 * rechnen" (UX-Gutachten 3, B-3).
 *
 * „Preis fehlt" (gaps) and „Kalkulation unvollständig" (blockers) are two
 * independent banners, not an either/or — a calculation can carry a
 * hard-stop blocker and an unrelated soft gap at once (N-16), and each needs
 * its own sentence. Walls without a measure are neither (E-65): one hint per
 * wall, said as a quiet count of their own while the release is still to come.
 *
 * Every notice reveals (BW-6) — but only the ones that *arrive*: a calculation
 * opened with „Preis fehlt“ or „Angebot ist raus“ already standing must not
 * slide its table, totals and approval down on first paint, so the scope below
 * shows what was there at mount at once and opens what comes after an answer.
 */
export function CalculationBanner({
  calc,
  detail,
  frozen,
  unreachable,
  busy,
  onRerun,
}: {
  calc: CalculationOut;
  detail: CaseDetailOut;
  frozen: boolean;
  /** The 503's sentence while the last run found no mirror, else `null`. */
  unreachable: string | null;
  busy: boolean;
  onRerun: () => void;
}) {
  const stale = calc.stale || calc.planVersionNo !== detail.latestVersionNo;
  // S-13 (Welle 0, 18.10.2026): on a released Vorgang the server refuses a
  // rerun („zieh die Freigabe zurück, bevor du neu rechnest“) — the same
  // status the server asks, so the button is never one that only earns a 409.
  const released = detail.status === "quote_approved";
  const review = isReleased(calc) ? calc.review : null;
  const age = calc.priceSource.ageDays;
  const syncedAt = calc.priceSource.syncedAt;
  // A rerun is the routine next step after the plan changed (UX-Gutachten 3,
  // B-3): its notice is a neutral one, and „Neu rechnen" the one main action
  // of the area, in K60 red (design 2026-10-24 §8.2) — a status-red notice
  // for it made a routine step look like a fault.
  const rerunMain = (
    <Button variant="primary" disabled={busy} onClick={onRerun}>
      Neu rechnen
    </Button>
  );
  // Ink, never K60 red, where the notice is a conflict — an approval that no
  // longer holds: status red and brand red never meet on one surface (CI
  // §2.1, controller ruling 26.10.2026) — in the dark the notice's ground and
  // K60 red are one hue. `tinte` is the second strong action (§8.2).
  const rerunInk = (
    <Button variant="tinte" disabled={busy} onClick={onRerun}>
      Neu rechnen
    </Button>
  );
  // One rerun for the notices above: the first chain below (stale with or
  // without an approval, „Erst neu rechnen“) shows it at most once; the
  // changed price state only carries it when that chain does not — restored
  // after a cancel on a newer price state, both ask for the same rerun, and
  // one button answers both.
  const rerunAbove = !frozen && (stale || needsRerunForStage(calc, detail.latestVersionNo, detail.status));
  // One hint per wall, so the hints are the count (E-65). Not once released —
  // the walls are ticked by then, as the gaps' notice knows too.
  const unmeasuredWalls = calc.hints.filter((hint) => isWallHint(hint.rule)).length;

  return (
    <FirstPaintScope>
      {unreachable !== null ? (
        // `live={false}` (fix round 2, item 8): this same sentence now also
        // reaches `CalculationScreen`'s persistent live line
        // (`runFailureLine` already turns this error into it) — carrying
        // `role="status"` here too would say it twice, and a screen reader
        // reacts to a change inside an existing region, not to this one
        // freshly mounting already full of text.
        <StateMessage reveal tone="conflict" title="Preisquelle nicht verfügbar" live={false}>
          {unreachable}
        </StateMessage>
      ) : null}

      {frozen ? (
        <StateMessage reveal title="Angebot ist raus">
          {/* „Neu öffnen“ does not exist (V-5): the way the board and the
              record name is the one there is. */}
          Der Vorgang ist versendet — Änderungen über einen neuen Vorgang. Die Kalkulation bleibt, wie
          sie freigegeben wurde.
        </StateMessage>
      ) : stale && review !== null ? (
        // An approval that no longer holds: a conflict, with the ink rerun.
        <StateMessage
          reveal
          tone="conflict"
          title={`Freigabe vom ${formatDateTime(review.at)} galt für Stand ${calc.planVersionNo}`}
          action={rerunInk}
        >
          {`Aktuell ist Stand ${detail.latestVersionNo}. Neu rechnen und erneut freigeben.`}
        </StateMessage>
      ) : stale ? (
        // The plan moved on and nothing was approved yet: the next step, not
        // a fault (UX-Gutachten 3, B-3).
        <StateMessage reveal title="Stand veraltet" action={rerunMain}>
          {`Diese Kalkulation gilt für Stand ${calc.planVersionNo}, der Vorgang steht bei Stand ${detail.latestVersionNo}. Freigabe erst nach dem Neurechnen.`}
        </StateMessage>
      ) : needsRerunForStage(calc, detail.latestVersionNo, detail.status) ? (
        // B-1 (screen check 15.10.2026): restored after a cancel, the Vorgang
        // is back in planning on the same Stand — the figures still hold, the
        // stage does not, and „Freigeben“ has no way from there. „Neu
        // rechnen“ is the one gesture: the same parameters on the same price
        // state answer with this very calculation and move the stage on
        // (Task 4 ruling). Never run on opening (D-22). A routine step, said
        // as one (UX-Gutachten 3, B-3).
        <StateMessage reveal title="Erst neu rechnen" action={rerunMain}>
          Der Vorgang steht wieder in der Planung. Freigabe erst nach dem Neurechnen — bei gleichem
          Preisstand bleibt die Kalkulation dieselbe.
        </StateMessage>
      ) : null}

      {/* A row priced by an engine that has changed since, on the latest Stand
          and not released: nothing above offers the rerun, and a rule the
          engine changed meanwhile (E-65) stays out of reach without one
          (29.10.2026). A routine step, said as one (UX-Gutachten 3, B-3). */}
      {!frozen && !released && calc.engineOutdated && !rerunAbove && !calc.priceSource.priceStateChanged ? (
        <StateMessage reveal title="Rechenregeln geändert" action={rerunMain}>
          Diese Kalkulation stammt aus einer älteren Fassung der Preisberechnung. Neu rechnen übernimmt die
          aktuelle.
        </StateMessage>
      ) : null}

      {/* Released, a new price state is blocked by the approval, not by a
          rerun — a conflict that only informs (S-13). Not released, the
          rerun is the way, said as the next step (UX-Gutachten 3, B-3). */}
      {!frozen && calc.priceSource.priceStateChanged ? (
        released ? (
          <StateMessage reveal tone="conflict" title="Preisstand hat sich geändert">
            Die Preislisten wurden seit dieser Kalkulation aktualisiert. Der Vorgang ist freigegeben — für neue
            Preise erst die Freigabe zurückziehen, dann neu rechnen.
          </StateMessage>
        ) : (
          <StateMessage reveal title="Preisstand hat sich geändert" action={rerunAbove ? undefined : rerunMain}>
            Die Preislisten wurden seit dieser Kalkulation aktualisiert. Freigabe erst nach dem Neurechnen.
          </StateMessage>
        )
      ) : null}

      {calc.status === "incomplete" && calc.gaps.length > 0 ? (
        <StateMessage reveal tone="conflict" title="Preis fehlt">
          {/* Once released, the gaps this banner would otherwise invite a
              person to tick are already ticked — the approval itself
              carries them (S-9). Inviting the same gesture again would be
              asking for something that already happened. */}
          {isReleased(calc) ? gapReleasedSentence(calc) : gapOpenSentence(calc)}
        </StateMessage>
      ) : null}

      {unmeasuredWalls > 0 && !isReleased(calc) ? (
        <StateMessage
          reveal
          title={`${count(unmeasuredWalls, "Wand", "Wände")} ohne Maß — ${WALLS_NOTICE_TAIL}`}
        />
      ) : null}

      {calc.status === "incomplete" && calc.blockers.length > 0 ? (
        <StateMessage reveal tone="conflict" title="Kalkulation unvollständig">
          {calc.blockers.map((blocker) => blocker.message).join(" · ")}
        </StateMessage>
      ) : null}

      {age !== null && syncedAt !== null && age >= PRICE_AGE_NOTICE_DAYS ? (
        <StateMessage reveal title={`Preisstand vom ${formatDateTime(syncedAt)}`}>
          {`Die Preislisten sind ${age} Tage alt. Die Freigabe nennt dieses Datum.`}
        </StateMessage>
      ) : null}
    </FirstPaintScope>
  );
}
